import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    User as UserIcon,
    ChevronRight,
    Crown,
    LayoutDashboard,
    Sun,
    MoonStar,
    Calendar,
    Orbit,
    MessageCircle,
    Heart,
    X,
    Linkedin,
    Mail,
    Instagram,
    Bell
} from 'lucide-react';
import { User } from '@store/authStore';

interface UserDropdownProps {
    user: User | null;
    onLogout?: () => void;
}

const UserDropdown = ({ user }: UserDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();

    // Prevent body scroll when sidebar is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Handle escape key to close sidebar
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsOpen(false);
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    const handleAction = (action: () => void) => {
        action();
        setIsOpen(false);
    };

    if (!user) return null;

    const Sidebar = () => (
        <>
            {/* Backdrop overlay */}
            <div
                className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                onClick={() => setIsOpen(false)}
            />

            {/* Sidebar panel */}
            <div
                className={`fixed top-0 right-0 h-full w-[280px] sm:w-80 bg-cosmic-900/95 backdrop-blur-2xl border-l border-white/10 shadow-2xl z-[9999] transform transition-transform duration-300 ease-out ${isOpen ? 'translate-x-0' : 'translate-x-full'
                    }`}
            >
                {/* Header with close button */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                    <div className="flex items-center gap-3">
                        {user.profile_picture || user.picture ? (
                            <img
                                src={user.profile_picture || user.picture}
                                alt="Profile"
                                className="w-10 h-10 rounded-full object-cover ring-2 ring-purple-500/30"
                            />
                        ) : (
                            <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-300 font-bold text-base uppercase ring-2 ring-purple-500/30">
                                {user.full_name?.[0] || user.username?.[0] || 'U'}
                            </div>
                        )}
                        <div className="min-w-0">
                            <p className="text-sm font-bold text-white truncate max-w-[150px] sm:max-w-[180px]">
                                {user.full_name || user.username || 'User'}
                            </p>
                            <p className="text-xs text-purple-300/70 truncate max-w-[150px] sm:max-w-[180px]">
                                {user.email || 'Seeker'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => setIsOpen(false)}
                        className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Scrollable content */}
                <div className="flex flex-col h-[calc(100%-80px)] overflow-y-auto">
                    <div className="p-3 flex-1">
                        {/* Profile Section */}
                        <div className="mb-2">
                            <p className="text-[10px] font-black uppercase tracking-widest text-purple-500/70 px-3 mb-2">Account</p>
                            <button
                                onClick={() => handleAction(() => navigate('/dashboard'))}
                                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.08] text-purple-100 transition-colors text-sm font-bold group"
                            >
                                <div className="p-2 rounded-lg bg-purple-500/10 text-purple-400 group-hover:bg-purple-500/20 transition-colors">
                                    <LayoutDashboard size={18} />
                                </div>
                                Dashboard
                            </button>

                            <button
                                onClick={() => handleAction(() => navigate('/profile'))}
                                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.08] text-purple-100 transition-colors text-sm font-bold group"
                            >
                                <div className="p-2 rounded-lg bg-purple-500/10 text-purple-400 group-hover:bg-purple-500/20 transition-colors">
                                    <UserIcon size={18} />
                                </div>
                                My Profile
                            </button>

                            <button
                                onClick={() => handleAction(() => navigate('/dashboard/notifications'))}
                                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.08] text-purple-100 transition-colors text-sm font-bold group"
                            >
                                <div className="p-2 rounded-lg bg-purple-500/10 text-purple-400 group-hover:bg-purple-500/20 transition-colors">
                                    <Bell size={18} />
                                </div>
                                Notifications
                            </button>

                            <button
                                onClick={() => handleAction(() => navigate('/discussion'))}
                                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.08] text-purple-100 transition-colors text-sm font-bold group"
                            >
                                <div className="p-2 rounded-lg bg-purple-500/10 text-purple-400 group-hover:bg-purple-500/20 transition-colors">
                                    <MessageCircle size={18} />
                                </div>
                                Discussion
                            </button>

                            {user.is_admin && (
                                <button
                                    onClick={() => handleAction(() => navigate('/admin'))}
                                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.08] text-purple-100 transition-colors text-sm font-bold group"
                                >
                                    <div className="p-2 rounded-lg bg-yellow-500/10 text-yellow-500 group-hover:bg-yellow-500/20 transition-colors">
                                        <Crown size={18} />
                                    </div>
                                    Admin
                                </button>
                            )}
                        </div>

                        <div className="h-px bg-white/10 my-3" />

                        {/* Services Section */}
                        <div className="mb-2">
                            <p className="text-[10px] font-black uppercase tracking-widest text-purple-500/70 px-3 mb-2">Free Services</p>
                            <button
                                onClick={() => handleAction(() => navigate('/free-kundli'))}
                                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.08] text-purple-100 transition-colors text-sm font-bold group"
                            >
                                <div className="p-2 rounded-lg bg-purple-500/10 text-purple-400 group-hover:bg-purple-500/20 transition-colors">
                                    <Sun size={18} />
                                </div>
                                Kundli
                            </button>

                            <button
                                onClick={() => handleAction(() => navigate('/kundli-matching'))}
                                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.08] text-purple-100 transition-colors text-sm font-bold group"
                            >
                                <div className="p-2 rounded-lg bg-pink-500/10 text-pink-400 group-hover:bg-pink-500/20 transition-colors">
                                    <Heart size={18} />
                                </div>
                                Matching
                            </button>

                            <button
                                onClick={() => handleAction(() => navigate('/horoscope'))}
                                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.08] text-purple-100 transition-colors text-sm font-bold group"
                            >
                                <div className="p-2 rounded-lg bg-purple-500/10 text-purple-400 group-hover:bg-purple-500/20 transition-colors">
                                    <MoonStar size={18} />
                                </div>
                                Horoscopes
                            </button>

                            <button
                                onClick={() => handleAction(() => navigate('/panchang'))}
                                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.08] text-purple-100 transition-colors text-sm font-bold group"
                            >
                                <div className="p-2 rounded-lg bg-purple-500/10 text-purple-400 group-hover:bg-purple-500/20 transition-colors">
                                    <Calendar size={18} />
                                </div>
                                Panchang
                            </button>

                            <button
                                onClick={() => handleAction(() => navigate('/transit-search'))}
                                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.08] text-purple-100 transition-colors text-sm font-bold group"
                            >
                                <div className="p-2 rounded-lg bg-indigo-500/10 text-indigo-400 group-hover:bg-indigo-500/20 transition-colors">
                                    <Orbit size={18} />
                                </div>
                                Transit Search
                            </button>

                        </div>
                    </div>

                    {/* Connect & Support at bottom */}
                    <div className="p-3 border-t border-white/10 mt-auto">

                        {/* Social Media Icons */}
                        <div className="mb-3">
                            <div className="flex justify-center gap-3 px-3">
                                <a
                                    href="https://www.linkedin.com/company/bhrigugpt"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-10 h-10 rounded-full bg-white/[0.08] flex items-center justify-center border border-white/10 hover:bg-purple-600 hover:text-white hover:border-purple-600 transition-all duration-300 text-purple-300"
                                >
                                    <Linkedin size={18} />
                                </a>
                                <a
                                    href="mailto:support@bhrigugpt.com"
                                    className="w-10 h-10 rounded-full bg-white/[0.08] flex items-center justify-center border border-white/10 hover:bg-purple-600 hover:text-white hover:border-purple-600 transition-all duration-300 text-purple-300"
                                >
                                    <Mail size={18} />
                                </a>
                                <a
                                    href="https://www.instagram.com/bhrigugpt"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-10 h-10 rounded-full bg-white/[0.08] flex items-center justify-center border border-white/10 hover:bg-purple-600 hover:text-white hover:border-purple-600 transition-all duration-300 text-purple-300"
                                >
                                    <Instagram size={18} />
                                </a>
                            </div>
                        </div>

                        <div className="h-px bg-white/10 my-3" />

                        {/* Support Links */}
                        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 px-3">
                            <button
                                onClick={() => handleAction(() => navigate('/contact'))}
                                className="text-[11px] text-purple-300/60 hover:text-purple-300 transition-colors"
                            >
                                Contact
                            </button>
                            <button
                                onClick={() => handleAction(() => navigate('/terms'))}
                                className="text-[11px] text-purple-300/60 hover:text-purple-300 transition-colors"
                            >
                                Terms
                            </button>
                            <button
                                onClick={() => handleAction(() => navigate('/privacy'))}
                                className="text-[11px] text-purple-300/60 hover:text-purple-300 transition-colors"
                            >
                                Privacy
                            </button>
                            <button
                                onClick={() => handleAction(() => navigate('/refunds'))}
                                className="text-[11px] text-purple-300/60 hover:text-purple-300 transition-colors"
                            >
                                Refunds
                            </button>
                            <a
                                href="https://bhrigugpt.hyperping.app"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setIsOpen(false)}
                                className="text-[11px] text-purple-300/60 hover:text-purple-300 transition-colors"
                            >
                                Status
                            </a>
                        </div>

                        {/* Made with love */}
                        <div className="flex items-center justify-center gap-1.5 mt-4 text-purple-100/50 text-[10px] uppercase tracking-widest font-sans">
                            Made with <Heart size={10} className="text-purple-400 fill-purple-400/30" /> for seekers
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    return (
        <>
            <button
                onClick={() => setIsOpen(true)}
                className="flex items-center justify-center gap-1.5 md:gap-2 pl-1.5 pr-2.5 md:pl-2 md:pr-4 h-8 md:h-auto md:py-1.5 bg-white/[0.08] border border-white/20 rounded-full hover:bg-white/[0.15] hover:border-purple-400/50 transition-colors duration-200 group cursor-pointer"
            >
                {user.profile_picture || user.picture ? (
                    <img
                        src={user.profile_picture || user.picture}
                        alt="Profile"
                        className="w-6 h-6 rounded-full object-cover"
                    />
                ) : (
                    <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-300 font-bold text-xs uppercase transition-all">
                        {user.full_name?.[0] || user.username?.[0] || 'U'}
                    </div>
                )}
                <span className="hidden sm:block text-xs md:text-sm font-bold text-white max-w-[80px] truncate tracking-wide">
                    {user.full_name?.split(' ')[0] || user.username || 'User'}
                </span>
                <ChevronRight size={14} className="text-white/60 transition-transform" />
            </button>

            {createPortal(<Sidebar />, document.body)}
        </>
    );
};

export default UserDropdown;
